import { HomeworkStatusChip } from "@/components/homework/HomeworkStatusChip";
import { CARD_HEIGHT, SlideScoreReadOnly } from "@/components/readonly";
import { getScoreColor } from "@/components/readonly/scoring";
import { AppButton } from "@/components/teacher/AppButton";
import { StatPill } from "@/components/teacher/StatPill";
import { type Dictionary, type Locale, format, formatYearMonthDay, useLocale, useT } from "@/i18n";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { HiChevronLeft, HiChevronRight, HiPause, HiPlay } from "react-icons/hi";

const DEFAULT_AUTOPLAY_SECONDS = 3;

/** One stored play, as either homework query returns it. */
export interface ReviewPlay {
  id: string;
  slideId: string;
  slideName: string;
  score?: number | null | undefined;
  promptSnapshot: unknown;
  response: unknown;
  recordingUrls?: unknown;
  imageUrls?: unknown;
}

/** What a review needs from an assignment, however it was fetched. */
export interface ReviewHomework {
  title: string;
  dueAt: Date | string;
  /** Named in the facts panel. Null where the account is gone, so the panel just drops the line. */
  student?: { name: string; username: string } | null | undefined;
  completedSlides: number;
  totalSlides: number;
  score?: number | null | undefined;
  isComplete?: boolean | undefined;
  isLate?: boolean | undefined;
  assignedAt?: Date | string | null | undefined;
  startedAt?: Date | string | null | undefined;
  lastPlayedAt?: Date | string | null | undefined;
  slides: { id: string; name: string; breadcrumb: string[] }[];
  plays: ReviewPlay[];
}

/** One assigned slide next to the play that answered it, if there is one. */
interface SlideRow {
  slideId: string;
  name: string;
  /** Where the slide sits in the content tree. Empty when nothing knows. */
  breadcrumb: string[];
  play: ReviewPlay | null;
}

/**
 * The assignment's dates, in the order they happen. "Submitted" is the last play of a finished
 * assignment; an unfinished one gets "Last played" instead, which is not a submission.
 */
function timeline(t: Dictionary, locale: Locale, homework: ReviewHomework): string {
  const on = (value: Date | string) => formatYearMonthDay(locale, new Date(value));
  const parts = [
    homework.assignedAt && format(t["review.assigned"], { date: on(homework.assignedAt) }),
    homework.startedAt && format(t["review.started"], { date: on(homework.startedAt) }),
    homework.lastPlayedAt &&
      format(t[homework.isComplete ? "review.submitted" : "review.lastPlayed"], {
        date: on(homework.lastPlayedAt),
      }),
  ];
  return parts.filter(Boolean).join(" · ");
}

/**
 * Every assigned slide in the teacher's order, each carrying the student's latest play. A slide
 * deleted since it was assigned is gone from `slides`, so its play is appended rather than
 * dropped — the homework counts it, and hiding it here would contradict the progress shown above.
 */
function buildRows(homework: ReviewHomework): SlideRow[] {
  const latestBySlide = new Map<string, ReviewPlay>();
  for (const play of homework.plays) latestBySlide.set(play.slideId, play);

  const assigned = homework.slides.map((slide) => ({
    slideId: slide.id,
    name: slide.name,
    breadcrumb: slide.breadcrumb,
    play: latestBySlide.get(slide.id) ?? null,
  }));

  const assignedIds = new Set(assigned.map((row) => row.slideId));
  const orphaned = [...latestBySlide.values()]
    .filter((play) => !assignedIds.has(play.slideId))
    .map((play) => ({ slideId: play.slideId, name: play.slideName, breadcrumb: [], play }));

  return [...assigned, ...orphaned];
}

/**
 * Where the slide sits in the content tree, innermost last. A teacher reporting a slide quotes
 * this from a screenshot, so it wraps rather than scrolling or truncating: every crumb has to be
 * legible on a phone, and the slide's own name is the part that stands out.
 */
function SlideTrail({ path, name }: { path: string[]; name: string }) {
  const t = useT();

  return (
    <nav
      aria-label={t["review.slideLocation"]}
      className="flex min-w-0 flex-wrap items-center gap-x-0.5 caption leading-snug"
    >
      {path.map((crumb, index) => {
        // A trail is fixed and never reorders, so position is a crumb's identity.
        const crumbKey = `crumb-${index}`;
        return (
          <span key={crumbKey} className="text-app-muted">
            {crumb}
            <HiChevronRight className="mx-px inline h-3 w-3 align-[-1px]" aria-hidden="true" />
          </span>
        );
      })}
      <span className="font-semibold text-app-ink">{name}</span>
    </nav>
  );
}

/** One assignment, replayed a slide at a time from the plays the student left behind. */
export function HomeworkReview({ homework }: { homework: ReviewHomework }) {
  const t = useT();
  const locale = useLocale();
  const rows = useMemo(() => buildRows(homework), [homework]);
  const dates = timeline(t, locale, homework);
  const hasScore = typeof homework.score === "number";

  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const selected = rows.find((row) => row.slideId === selectedSlideId) ?? rows[0] ?? null;
  const selectedIndex = rows.findIndex((row) => row.slideId === selected?.slideId);
  const reviewRef = useRef<HTMLDivElement>(null);

  /** A cleared or out-of-range box holds the default rather than stalling or racing autoplay. */
  const autoplayMs = DEFAULT_AUTOPLAY_SECONDS * 1000;

  /** Any hand-picked slide ends autoplay, so the teacher keeps whatever they chose to look at. */
  const select = (slideId: string) => {
    setIsPlaying(false);
    setSelectedSlideId(slideId);
  };

  /** Steps along the teacher's slide order, bringing the top of the answer back into view. */
  const step = (offset: number) => {
    const next = rows[selectedIndex + offset];
    if (!next) return;
    select(next.slideId);
  };

  /** Playing from the last slide starts the assignment over rather than doing nothing. */
  const togglePlay = () => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    if (selectedIndex >= rows.length - 1) setSelectedSlideId(rows[0]?.slideId ?? null);
    setIsPlaying(true);
  };

  /** Autoplay advances from whatever is selected, so it resumes wherever the teacher left off. */
  useEffect(() => {
    if (!isPlaying) return;
    const next = rows[selectedIndex + 1];
    if (!next) {
      setIsPlaying(false);
      return;
    }
    const timeoutId = setTimeout(() => setSelectedSlideId(next.slideId), autoplayMs);
    return () => clearTimeout(timeoutId);
  }, [isPlaying, selectedIndex, rows, autoplayMs]);

  return (
    <div ref={reviewRef} className="space-y-4">
      {rows.length > 1 && (
        <div className="app-card flex items-center justify-center gap-3 py-1">
          <span className="app-label truncate">
            {format(t["review.slideOfTotal"], {
              current: selectedIndex + 1,
              total: rows.length,
            })}
          </span>
          <AppButton
            tone="quiet"
            className="h-8 w-8 !px-0"
            onClick={togglePlay}
            aria-label={isPlaying ? t["review.pauseAutoplay"] : t["review.playInOrder"]}
          >
            {isPlaying ? (
              <HiPause className="h-7 w-7" aria-hidden="true" />
            ) : (
              <HiPlay className="h-7 w-7" aria-hidden="true" />
            )}
          </AppButton>
        </div>
      )}

      {/* The arrows overlay the answer's edges instead of taking width from it, and stay stuck
          to the top of the row so a tall answer never scrolls them out of reach. The row caps at
          the read-only card's own 480px over the 90% it is given, so the card centres on desktop. */}
      <div className="relative mx-auto w-full max-w-[534px]">
        {rows.length > 1 && (
          <div className="absolute inset-y-0 left-0 z-10 flex w-11 justify-center">
            <AppButton
              tone="glass"
              className="sticky top-4 h-11 w-11 !px-0 shadow-app-card"
              disabled={selectedIndex <= 0}
              onClick={() => step(-1)}
              aria-label={t["review.previousSlide"]}
            >
              <HiChevronLeft className="h-6 w-6" aria-hidden="true" />
            </AppButton>
          </div>
        )}

        <div className="mx-auto w-[90%]">
          {rows.length === 0 ? (
            <p className="app-card p-4 body-sm text-app-muted sm:p-6">{t["review.noSlides"]}</p>
          ) : selected?.play ? (
            <SlideScoreReadOnly key={selected.play.id} record={selected.play} />
          ) : (
            <div className="app-card p-4 space-y-2 sm:p-6" style={{ minHeight: CARD_HEIGHT }}>
              <h2 className="app-title">{selected?.name}</h2>
              <p className="body-sm text-app-muted">{t["review.notPlayed"]}</p>
            </div>
          )}
        </div>

        {rows.length > 1 && (
          <div className="absolute inset-y-0 right-0 z-10 flex w-11 justify-center">
            <AppButton
              tone="glass"
              className="sticky top-4 h-11 w-11 !px-0 shadow-app-card"
              disabled={selectedIndex >= rows.length - 1}
              onClick={() => step(1)}
              aria-label={t["review.nextSlide"]}
            >
              <HiChevronRight className="h-6 w-6" aria-hidden="true" />
            </AppButton>
          </div>
        )}
      </div>

      {selected && (
        <div className="app-card px-3 py-1.5">
          <SlideTrail path={selected.breadcrumb} name={selected.name} />
        </div>
      )}

      {/* The assignment facts sit below the answer, so the answer opens at the top of the page
          instead of under a screen of them. The score leads: it is what a teacher came for, and
          it is coloured on the same thresholds as the per-slide scores above it. */}
      <div className="app-card p-3 sm:px-4">
        <div className="flex items-start justify-between gap-3 sm:gap-5">
          <div className="min-w-0 flex-1 space-y-2">
            {homework.student && (
              <p className="app-value truncate">
                {homework.student.name}
                <span className="caption ml-1.5 font-normal text-app-muted">
                  {homework.student.username}
                </span>
              </p>
            )}
            <div className="flex items-start justify-between gap-2">
              <h1 className="app-title line-clamp-2">{homework.title}</h1>
              {typeof homework.isComplete === "boolean" && (
                <HomeworkStatusChip
                  isComplete={homework.isComplete}
                  isLate={homework.isLate ?? false}
                />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <StatPill tone={homework.isLate && !homework.isComplete ? "overdue" : "aqua"}>
                {format(t["review.dueOn"], {
                  date: formatYearMonthDay(locale, new Date(homework.dueAt)),
                })}
              </StatPill>
              <StatPill tone={homework.isComplete ? "mint" : "aqua"}>
                {format(t["review.slidesDone"], {
                  completed: homework.completedSlides,
                  total: homework.totalSlides,
                })}
              </StatPill>
            </div>
          </div>

          <div className="shrink-0 text-right">
            <p className="app-label">{t["review.score"]}</p>
            <p
              className="app-metric !text-[44px] !leading-none"
              style={{ color: hasScore ? getScoreColor(homework.score ?? 0) : undefined }}
            >
              {hasScore ? Math.round(homework.score ?? 0) : "—"}
            </p>
          </div>
        </div>
        {dates && <p className="caption mt-2 text-app-muted">{dates}</p>}
      </div>
    </div>
  );
}
